import { DOCUMENT, ViewportScroller } from '@angular/common';
import { Injectable, Inject } from '@angular/core';
import { Actions, createEffect, ofType, EffectNotification } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {

    Observable, switchMap, filter, map, tap, distinctUntilChanged,

    exhaustMap,
    takeUntil,
    of,
    catchError,
    concatMap,
} from 'rxjs';

import { GridDefault } from '../models/custom-category.models';

import {
    InitCustomCategoryGrid,
    ChangeCustomCategory,
    SetGridTypeCustomCategoryGrid,
    LoadFirstPageCustomCategory,
    LoadNextPageCustomCategory,
    LoadNextPageCustomCategorySuccess,
    LoadLastPageCustomCategorySuccess,
    ScrollToCustomCategoryPositionId,
    LoadCustomCategoryProducts,
    LoadCustomCategoryProductsSuccess,
    DisableCustomCategoryGridEffects,
    InitCustomCategoryGridEffects,
    LoadNextPageCustomCategoryError,
} from './custom-category-grid.actions';
import { getCustomCategory, getCustomCategoryCategoryId } from './custom-category-grid.selectors';

import { Category } from '@core/domain/models/category.models';
import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { ProductPublicModels } from '@features/products/product';
import { ProductsArrayFeaturePublicApi, ProductsArrayPublicModels, ProductsArrayPublicSelectors } from '@features/products/productsArray';
import { ItxObservabilityCommands } from '@frontend/libs/itx-observability-core';
import {
    CustomCategoryGridService,
} from '@pages/category/modules/grid-custom-category/services/custom-category-grid.service';
import { CustomCategories, getCategory } from '@pages/category/public_api';
import { SetProductsId } from '@pages/category/store/category-page.actions';
import { getGridType } from '@pages/category/store/grid-type/grid-type.selectors';
import { getPrevRouteProduct } from '@pages/product/store/product-page.selectors';
import { getLastSectionSelector } from '@shared/menu-categories/store/menu-categories.selectors';
import { GridTypes } from '@shared/models/grid-type.model';
import { PreviousRouteNavigation, ProductNavigationData, getProductNavigationData } from '@shared/navigation';
import { ProductPaginationService } from '@shared/product/services/product-pagination.service';

@Injectable()
export class CustomCategoryGridEffects {
    private readonly getCategory$: Observable<Category> = this.store.select(getCategory);

    private readonly getGridCategory$: Observable<string> = this.store.select(getCustomCategoryCategoryId);

    private readonly getGridDefault$: Observable<GridDefault> = this.store.select(getCustomCategory);

    private readonly getGridDefaultCustomCategory$: Observable<GridDefault> = this.store.select(getCustomCategory);

    private readonly getGridType$: Observable<GridTypes> = this.store.select(getGridType);

    private readonly getPrevRouteProduct$: Observable<PreviousRouteNavigation> = this.store.select(getPrevRouteProduct);

    private readonly getProductNavigation$: Observable<ProductNavigationData> = this.store.select(getProductNavigationData);

    constructor(
        private readonly actions$: Actions,
        private readonly categoryGridService: CustomCategoryGridService,
        private readonly productPaginationService: ProductPaginationService,
        private readonly productsArrayPublicApi: ProductsArrayFeaturePublicApi,
        private readonly store: Store,
        private readonly viewportScroller: ViewportScroller,
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly platformService: CorePlatformService
    ) { }

    public initModule$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitCustomCategoryGrid),
            switchMap(() => this.getCategory$),
            concatLatestFrom(() => this.getGridCategory$),
            filter(([category, lastCategoryId]) => !!category.id && category.id !== lastCategoryId),
            map(([category]) => ChangeCustomCategory({ category })),
        );
    });

    // END Test AB Zoom categorías

    public initCategoryGrid$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitCustomCategoryGrid),
            map(() => SetGridTypeCustomCategoryGrid({ initGridType: true }))
        );
    });

    // Efecto de inicialización de la Grid de ID's
    public loadFirstPageOnChangeCategory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ChangeCustomCategory, SetProductsId),
            concatMap(() => this.categoryGridService.mapStateGridDefault()),
            map((gridDefault: GridDefault) => LoadFirstPageCustomCategory({ grid: gridDefault })),
        );
    });

    public loadLastPageCustomCategory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadNextPageCustomCategory),
            concatLatestFrom(() => this.getGridDefault$),
            filter(([_, grid]) => {
                return grid.pageProductIds.length >= grid.idsFiltered.length;
            }),
            distinctUntilChanged(([, b], [, d]) => b.currentPage === d.currentPage),
            map(() => LoadLastPageCustomCategorySuccess())
        );
    });

    public whenGridTypeChangeLoadMoreProducts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetGridTypeCustomCategoryGrid),
            filter(({ initGridType }) => !initGridType),
            map(() => LoadNextPageCustomCategory()),
        );
    });

    public seeIfReturnToNavigateProduct$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitCustomCategoryGrid),
            switchMap(() => this.getCategory$),
            concatLatestFrom(() => this.getGridCategory$),
            filter(([category, lastCategoryId]) => !!category.id && category.id === lastCategoryId),
            concatLatestFrom(() => [this.getPrevRouteProduct$, this.getProductNavigation$]),
            map(([[category], prevRouteNavigationData, productNavigationData]): string => {
                if (productNavigationData && category
                    && prevRouteNavigationData.page === 'ItxCategoryPage'
                    && category.id === productNavigationData.categoryId) {
                    return productNavigationData.productId;
                }
                return null;
            }),
            filter(productId => !!productId),
            map((productId) => ScrollToCustomCategoryPositionId({ id: productId })),
        );
    });

    public scrollToPositionId$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ScrollToCustomCategoryPositionId),
            filter(() => this.platformService.isBrowser()),
            concatLatestFrom(() => this.getProductNavigation$),
            map(([_, nav]) => {
                const pos = nav?.previousRoute?.scrollPosition;
                if (pos) {
                    const config = { attributes: true, childList: true, subtree: true };
                    const observer = new MutationObserver(() => {
                        if (this.document.body.scrollHeight >= pos) {
                            this.viewportScroller.scrollToPosition([0, pos]);
                            observer.disconnect();
                        }
                    });
                    observer.observe(this.document, config);
                }
            }),
        );
    }, { dispatch: false });

    // ############## SPECIFIC CUSTOM CATEGORY EFFECTS ##############

    public loadCustomCategory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ChangeCustomCategory),
            map(({ category }) => CustomCategoryGridService.getCustomCategory(category)),
            filter((name) => !!name),
            map((name) => LoadCustomCategoryProducts({ name }))
        );
    });

    public loadFirstPageCustomCategory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCustomCategoryProductsSuccess),
            concatLatestFrom(() => this.getGridType$),
            map(([categoryProducts, gridType]) =>
                CustomCategoryGridService.mapStateGridCustomCategory(
                    categoryProducts.data,
                    gridType
                )),
            map((grid) => LoadFirstPageCustomCategory({ grid })),
        );
    });

    public loadCustomCategoryProducts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                LoadFirstPageCustomCategory,
                LoadNextPageCustomCategory
            ),
            concatLatestFrom(() => [
                this.store.select(getCategory),
                this.getGridDefaultCustomCategory$,
                this.getGridType$,
                this.store.select(ProductsArrayPublicSelectors.getProductsList),
                this.store.select(getCmsConfigValue('related_products_custom_grid_config')),
            ]),
            filter(([_, __, grid]) => grid.currentPage < grid.totalPagesIds),
            switchMap(([_, category, grid, gridType, productsList, config]) => {
                try {
                    const pageProductIds = CustomCategoryGridService.sliceIdsWithPageSize(
                        gridType,
                        grid.idsFiltered,
                        grid.currentPage
                    ).ids;
                    /* getProductsArray$ NO devuelve los productos que se le piden pero ya tiene cargados en la store por lo que tenemos que hacer
                        esto para recuperarlos y poder incluirlos al estado como "loaded" junto con el resto */
                    // eslint-disable-next-line max-len
                    const alreadyLoadedProducts: ProductPublicModels.PublicProduct[] = pageProductIds.reduce((acc, productId) => {
                        const productFound = productsList[productId.toString()];
                        if (productFound) acc.push(productFound);
                        return acc;
                    }, []);

                    return this.productsArrayPublicApi.getProductsArray$(
                        pageProductIds.join(','),
                        category.id
                    ).pipe(
                        map((requestedProducts) => {
                            const productsToFilter = [...alreadyLoadedProducts, ...requestedProducts];
                            const productsFiltered = CustomCategoryGridService.applyFiltersToProductList(
                                productsToFilter,
                                category.id,
                                config,
                            );

                            return productsFiltered.map((product) => Number.parseInt(product.id));
                        }),
                        map((ids) => LoadNextPageCustomCategorySuccess({ ids, page: grid.currentPage + 1 })),
                        catchError((error) => of(LoadNextPageCustomCategoryError({ error })))
                    );
                } catch (error) {
                    ItxObservabilityCommands.sendObservabilityLog(error, 'CustomCategoryGridEffects loadCustomCategoryProducts');
                    return of(LoadNextPageCustomCategoryError({ error }));
                }
            }),
        );
    });

    public addCustomCategoryProducts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadNextPageCustomCategorySuccess),
            tap(({ ids }) => {
                this.productPaginationService.addCategoryProducts(ids);
            }),
        );
    }, { dispatch: false });

    public onChangeCustomCategoryForceGrid$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ChangeCustomCategory),
            map(() => SetGridTypeCustomCategoryGrid({ initGridType: true })),
        );
    });

    /* ################################ */

    /* ################ EFECTOS ESPECÍFICOS DE CADA ENDPOINT ################ */

    public loadRfyPersonalizationCategoryProductIds$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCustomCategoryProducts),
            filter(({ name }) => name === CustomCategories.ITX_MECACO_RFY_PERSONALIZATION_CREATE),
            concatLatestFrom(() => [
                this.store.select(getLastSectionSelector),
                this.store.select(getCmsConfigValue('related_products_custom_grid_config')),
            ]),
            switchMap(([_, lastSection, config]) =>
                this.categoryGridService.fetchRecommendationsByRfy$(
                    lastSection,
                    config,
                )),
            map((productIds) => {
                const categoryGrids: ProductsArrayPublicModels.PublicCategoryPlatformStudio = {
                    filters: null,
                    gridElements: null,
                    productIds,
                    sortedProductIds: productIds,
                    sortedProductIdsByPricesAsc: productIds,
                    elementDefaultImageTypes: null,
                };
                return LoadCustomCategoryProductsSuccess({
                    data: [
                        {
                            categoryGrids: categoryGrids,
                            productsSource: CustomCategories.ITX_MECACO_RFY_PERSONALIZATION_CREATE,
                        },
                    ],
                });
            })
        );
    });

    /* ################################ */

    public ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): any {
        return this.actions$.pipe(
            ofType(InitCustomCategoryGridEffects),
            exhaustMap(() => resolvedEffects$.pipe(
                takeUntil(this.actions$.pipe(ofType(DisableCustomCategoryGridEffects)))
            ))
        );
    }
}
